import React from 'react';

interface GameCardProps {
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
  disabledText?: string;
  color: string;
  icon?: string;
  skill?: string;
}

const themeMap: Record<string, { text: string; banner: string; button: string; chip: string }> = {
  red: { text: 'text-rose-600', banner: 'from-rose-400 via-pink-400 to-orange-300', button: 'from-rose-500 to-orange-400', chip: 'text-rose-700' },
  blue: { text: 'text-blue-600', banner: 'from-sky-400 via-blue-400 to-indigo-400', button: 'from-blue-500 to-cyan-400', chip: 'text-blue-700' },
  purple: { text: 'text-violet-600', banner: 'from-violet-400 via-fuchsia-400 to-pink-300', button: 'from-violet-500 to-fuchsia-400', chip: 'text-violet-700' },
  green: { text: 'text-emerald-600', banner: 'from-emerald-400 via-teal-400 to-cyan-300', button: 'from-emerald-500 to-teal-400', chip: 'text-emerald-700' },
  orange: { text: 'text-orange-600', banner: 'from-amber-300 via-orange-400 to-rose-300', button: 'from-orange-500 to-amber-400', chip: 'text-orange-700' },
  default: { text: 'text-gray-700', banner: 'from-gray-300 to-gray-400', button: 'from-gray-500 to-gray-400', chip: 'text-gray-700' },
};

const GameCard: React.FC<GameCardProps> = ({ title, description, onClick, disabled = false, disabledText, color, icon = '🎮', skill }) => {
  const theme = themeMap[color] || themeMap.default;
  // Stagger the idle float so the whole grid doesn't bob in sync.
  const delay = `${(title.length % 7) * 0.45}s`;

  return (
    <div className={`group relative bg-white rounded-3xl shadow-md border-4 border-white flex flex-col overflow-hidden transition-all duration-200 ${disabled ? 'opacity-80' : 'hover:shadow-2xl hover:-translate-y-1.5'}`}>
      <div className={`relative h-28 bg-gradient-to-br ${theme.banner} flex items-center justify-center overflow-hidden`}>
        <span className="absolute -left-4 -top-6 w-20 h-20 rounded-full bg-white/20" aria-hidden="true" />
        <span className="absolute right-6 -bottom-8 w-24 h-24 rounded-full bg-white/15" aria-hidden="true" />
        <span className="absolute right-16 top-3 w-4 h-4 rounded-full bg-white/40" aria-hidden="true" />
        <span className="fun-float inline-block" style={{ animationDelay: delay }}>
          <span className="fun-wiggle-hover inline-block text-6xl drop-shadow-lg" aria-hidden="true">{icon}</span>
        </span>
        {skill && (
          <span className={`absolute top-3 right-3 text-[11px] font-extrabold uppercase tracking-wide px-2.5 py-1 rounded-full bg-white/90 shadow-sm ${theme.chip}`}>
            {skill}
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-grow">
        <h3 className={`text-xl font-black mb-1 ${theme.text}`}>{title}</h3>
        <p className="text-gray-500 text-sm mb-4 flex-grow">{description}</p>
        <button
          onClick={onClick}
          className={`w-full px-4 py-2.5 text-white rounded-full font-extrabold text-lg flex items-center justify-center gap-2 shadow-md transition-transform ${disabled ? 'bg-gray-300 cursor-not-allowed' : `fun-shine bg-gradient-to-r ${theme.button} hover:scale-[1.03] active:scale-95`}`}
          disabled={disabled}
        >
          <span aria-hidden="true">▶</span> Play
        </button>
        {disabled && <p className="text-xs text-red-500 mt-2 text-center">{disabledText}</p>}
      </div>
    </div>
  );
};

export default GameCard;
